/**
 * @file serp-images.ts
 * @summary Pool d'images optimisées pour SERP Google/Bing
 *
 * Liste des 5 images mises à disposition de Google Images / Bing pour s'afficher
 * à côté de la description dans les résultats de recherche. Chaque entrée embarque
 * toutes les métadonnées SEO/GEO (titre, description, caption, dimensions, droits,
 * mots-clés CrossFit + Est lyonnais).
 *
 * Utilisé par :
 *   - `index.html` (graph ImageObject statique pour la home)
 *   - `src/pages/ChargesPage.tsx`, `src/pages/Lexique.tsx`
 *   - `src/components/LegalPage.tsx` (mentions, CGU, CGV, RGPD, cookies)
 *   - `scripts/generate-sitemap-images.mjs` (sitemap-images.xml)
 */

const SITE = "https://crossfitchistera.fr";

export interface SerpImage {
  slug: string;
  width: number;
  height: number;
  name: string;
  description: string;
  caption: string;
  alt: string;
}

export const SERP_IMAGES: SerpImage[] = [
  {
    slug: "coaching-collectif-crossfit-chistera-saint-pierre-de-chandieu",
    width: 1440,
    height: 1080,
    name: "Cours collectif CrossFit Chistera — Saint-Pierre-de-Chandieu",
    description:
      "Cours collectif coaché à la box CrossFit Chistera de Saint-Pierre-de-Chandieu (Est lyonnais) — briefing du WOD avec adhérents de Heyrieux, Chaponnay, Mions, Saint-Priest et Lyon.",
    caption:
      "Briefing d'un cours collectif CrossFit chez Chistera (Saint-Pierre-de-Chandieu, Lyon Est) — coach diplômé, fitness fonctionnel, communauté soudée.",
    alt: "Coach CrossFit Chistera briefant un cours collectif à la box de Saint-Pierre-de-Chandieu (Lyon Est)",
  },
  {
    slug: "duo-coachs-crossfit-chistera-est-lyonnais",
    width: 900,
    height: 1125,
    name: "Coachs CrossFit Chistera — box CrossFit Est lyonnais",
    description:
      "Les coachs CrossFit Chistera devant le logo de la box CrossFit de Saint-Pierre-de-Chandieu — coaching fitness fonctionnel pour Heyrieux, Chaponnay, Mions, Saint-Priest et l'Est lyonnais.",
    caption:
      "Anthony et Cédric, coachs de la box CrossFit Chistera (Saint-Pierre-de-Chandieu) — CrossFit Level 1/2, BPJEPS, Master STAPS.",
    alt: "Deux coachs CrossFit Chistera devant le logo de la box (Saint-Pierre-de-Chandieu, Est lyonnais)",
  },
  {
    slug: "communaute-adherents-box-crossfit-chistera-lyon-est",
    width: 1440,
    height: 1638,
    name: "Communauté adhérents — box CrossFit Chistera Lyon Est",
    description:
      "Photo de groupe des adhérents de la box CrossFit Chistera à Saint-Pierre-de-Chandieu : communauté fitness venue de Heyrieux, Chaponnay, Mions, Saint-Priest, Toussieu, Corbas et Lyon.",
    caption:
      "Communauté CrossFit Chistera réunie dans la box de Saint-Pierre-de-Chandieu (Est lyonnais) — événement interne et esprit famille.",
    alt: "Photo de groupe des adhérents CrossFit Chistera, box CrossFit de Saint-Pierre-de-Chandieu (Lyon Est)",
  },
  {
    slug: "cours-octobre-rose-crossfit-feminin-chistera-chandieu",
    width: 1440,
    height: 1916,
    name: "Cours Octobre Rose — CrossFit Chistera (Saint-Pierre-de-Chandieu)",
    description:
      "Cours collectif féminin Octobre Rose à la box CrossFit Chistera de Saint-Pierre-de-Chandieu — sport santé, prévention cancer du sein, communauté CrossFit Lyon Est (Heyrieux, Chaponnay, Mions, Saint-Priest).",
    caption:
      "Cours Octobre Rose 100 % féminin chez CrossFit Chistera — WOD solidaire au profit de la Ligue contre le cancer du Rhône.",
    alt: "Cours collectif féminin Octobre Rose à la box CrossFit Chistera de Saint-Pierre-de-Chandieu",
  },
  {
    slug: "logo-crossfit-chistera-box-crossfit-saint-pierre-de-chandieu",
    width: 1370,
    height: 1920,
    name: "Logo CrossFit Chistera — box CrossFit Saint-Pierre-de-Chandieu",
    description:
      "Logo officiel de CrossFit Chistera, box CrossFit affiliée à Saint-Pierre-de-Chandieu (Est lyonnais) — fitness fonctionnel, WOD, Hybrid, Kids, Seniors pour Heyrieux, Chaponnay, Mions, Saint-Priest et Lyon.",
    caption: "Logo CrossFit Chistera — box CrossFit affiliée Saint-Pierre-de-Chandieu (Lyon Est).",
    alt: "Logo CrossFit Chistera, box CrossFit affiliée à Saint-Pierre-de-Chandieu (Est lyonnais)",
  },
  {
    slug: "calendrier-2025-esprit-crossfit-chistera-ligue-cancer-rhone",
    width: 1200,
    height: 1200,
    name: "Calendrier Esprit CrossFit 2025 — CrossFit Chistera × Ligue contre le cancer du Rhône",
    description:
      "Calendrier caritatif « Esprit CrossFit 2025 » réalisé par les coachs et athlètes de CrossFit Chistera (box CrossFit, cross-training et fitness à Saint-Pierre-de-Chandieu) au profit de la Ligue contre le cancer du Rhône — projet solidaire porté par la communauté de Bonnefamille, Heyrieux, Chaponnay, Mions, Saint-Laurent-de-Mure, Saint-Bonnet-de-Mure, Saint-Priest, Corbas, Meyzieu, Diémoz et Lyon.",
    caption:
      "Calendrier « Esprit CrossFit 2025 » — vente caritative CrossFit Chistera au profit de la Ligue contre le cancer du Rhône (https://www.ligue-cancer.net/69-rhone).",
    alt: "Calendrier Esprit CrossFit 2025 de CrossFit Chistera vendu au profit de la Ligue contre le cancer du Rhône (box CrossFit, cross-training et fitness à Saint-Pierre-de-Chandieu, Est lyonnais)",
  },
  {
    slug: "cheque-1000-euros-ligue-cancer-rhone-crossfit-chistera",
    width: 1080,
    height: 1080,
    name: "Don de 1 000 € à la Ligue contre le cancer du Rhône — CrossFit Chistera",
    description:
      "Remise officielle du chèque de 1 000 € à la Ligue contre le cancer du Rhône par les adhérents et coachs de CrossFit Chistera, box de CrossFit, cross-training et fitness à Saint-Pierre-de-Chandieu — engagement sport santé rayonnant sur Bonnefamille, Heyrieux, Chaponnay, Mions, Saint-Laurent-de-Mure, Saint-Bonnet-de-Mure, Saint-Priest, Corbas, Meyzieu, Diémoz et Lyon.",
    caption:
      "Remise du chèque de 1 000 € à la Ligue contre le cancer du Rhône — bénéfice du calendrier « Esprit CrossFit 2025 » de CrossFit Chistera.",
    alt: "Adhérents et coachs CrossFit Chistera remettant un chèque de 1 000 € à la Ligue contre le cancer du Rhône",
  },
  {
    slug: "affiche-chistera-rose-wod-feminin-octobre-rose-saint-pierre-de-chandieu",
    width: 320,
    height: 372,
    name: "Chistera Rose — WOD 100 % féminin Octobre Rose à CrossFit Chistera",
    description:
      "Affiche officielle « Chistera Rose » : WOD 100 % féminin organisé à la box CrossFit Chistera de Saint-Pierre-de-Chandieu pour récolter des dons au profit de la Ligue contre le cancer du Rhône — événement Octobre Rose ouvert aux femmes de Bonnefamille, Heyrieux, Chaponnay, Mions, Saint-Laurent-de-Mure, Saint-Bonnet-de-Mure, Saint-Priest, Corbas, Meyzieu, Diémoz et Lyon.",
    caption:
      "Affiche « Chistera Rose » — WOD féminin Octobre Rose chez CrossFit Chistera, coaché par Marlène, au profit de la Ligue contre le cancer.",
    alt: "Affiche Chistera Rose — WOD 100 % féminin Octobre Rose à la box CrossFit Chistera de Saint-Pierre-de-Chandieu",
  },
];

export const SERP_IMAGE_URLS = SERP_IMAGES.map((i) => `${SITE}/images/serp/${i.slug}.jpg`);

/**
 * Renvoie un noeud @graph d'`ImageObject` pour Google / Bing — boost SERP image.
 * Chaque image porte droits (creator, license, copyrightHolder) + dimensions exactes.
 */
export function buildSerpImageGraph(licenseUrl = `${SITE}/legal/mentions`) {
  return {
    "@context": "https://schema.org",
    "@graph": SERP_IMAGES.map((i) => ({
      "@type": "ImageObject",
      contentUrl: `${SITE}/images/serp/${i.slug}.jpg`,
      url: `${SITE}/images/serp/${i.slug}.jpg`,
      name: i.name,
      description: i.description,
      caption: i.caption,
      width: i.width.toString(),
      height: i.height.toString(),
      creditText: "CrossFit Chistera",
      copyrightNotice: "© CrossFit Chistera",
      creator: { "@type": "Organization", name: "CrossFit Chistera", url: SITE },
      copyrightHolder: { "@type": "Organization", name: "CrossFit Chistera", url: SITE },
      license: licenseUrl,
      acquireLicensePage: licenseUrl,
    })),
  };
}
